import axios from 'axios';
import i18n, { getCurrentLanguage } from '../../i18n';
import HttpError, { newHttpError } from '@common/interfaces/api/http-error';
import ValidationResult from '@common/interfaces/api/validation-result';
import { accessTokenStorageName } from '../../store/general/general-slice-definitions';
import { searchHistoryStorageName } from '../../components/ui/SearchBar';
import { getCurrentPageName, trackEvent } from '../analytics';
import { AnalyticsEvent } from '../analytics-events';
import { extractErrorKeys, getErrorCategory, toEndpointTemplate } from '../analytics-helpers';
import { getAppVersion } from '../app-version';
import { setUpdateAvailable, setUpdateRequired } from '../app-update-state';

export const apiHost =
  process.env.REACT_APP_API_HOST ||
  `${window.location.protocol}//${window.location.hostname}${process.env.NODE_ENV !== 'production' ? ':8000' : ''}`;

export const baseUrlApiManagement = `${apiHost}/api`;

export const baseWebsocketUrl = apiHost.replace(/^http/, 'ws') + '/ws';

export const AUTH_SUBPROTOCOL = 'kemaro.auth.v1';
export const WS_CLOSE_UNAUTHENTICATED = 4401;
export const WS_CLOSE_FORBIDDEN = 4403;

export const websocketProtocols = (): string[] | undefined => {
  const token = getToken();
  return token ? [AUTH_SUBPROTOCOL, token] : undefined;
};
export const currentVersionManagement = 'v1.0';

export const managementApiInstance = axios.create({
  baseURL: baseUrlApiManagement,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': getCurrentLanguage().languageCode,
  },
});

export const getToken = () =>
  localStorage.getItem(accessTokenStorageName)
    ? localStorage.getItem(accessTokenStorageName)
    : null;

export const fetchAllStorageKeyPrefix = 'kemaro-cloud-fetch-all';

const removeFetchAllChoices = () => {
  Object.keys(localStorage)
    .filter((key) => key.startsWith(`${fetchAllStorageKeyPrefix}:`))
    .forEach((key) => localStorage.removeItem(key));
};

export const removeToken = () => {
  localStorage.removeItem(accessTokenStorageName);
  localStorage.removeItem(searchHistoryStorageName);
  removeFetchAllChoices();
};

export const getUrl = (endpoint: string) =>
  `${baseUrlApiManagement}/${currentVersionManagement}/${endpoint}/`;
export const getTestSocketUrl = (endpoint: string) => `http://localhost:8000/ws/${endpoint}/`;

export const externalApiSchemaPath = '/schema/';
export const externalApiSchemaUrl = `${baseUrlApiManagement}${externalApiSchemaPath}`;

managementApiInstance.interceptors.request.use(
  async (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = 'Token ' + token;
    }
    const appVersion = await getAppVersion();
    if (appVersion) {
      config.headers['X-App-Version'] = appVersion;
    }
    return config;
  },
  (error) => {
    Promise.reject(error);
  },
);

export const trackFailedRequest = (error: unknown) => {
  if (axios.isCancel(error) || !axios.isAxiosError(error)) return;
  const status = error.response?.status;
  trackEvent(AnalyticsEvent.ApiRequestFailed, {
    endpoint: toEndpointTemplate(error.config?.url),
    method: (error.config?.method ?? 'unknown').toUpperCase(),
    status_code: status ?? null,
    error_category: getErrorCategory(status),
    // Blob responses (media) cannot be read synchronously and hold no keys anyway.
    error_keys: error.response?.data instanceof Blob ? [] : extractErrorKeys(error.response?.data),
    page_name: getCurrentPageName(),
  });
};

managementApiInstance.interceptors.response.use(
  (response) => {
    const latestVersion = response.headers['x-app-update-available'];
    const storeUrl = response.headers['x-app-update-url'];
    if (latestVersion) {
      setUpdateAvailable(String(latestVersion), storeUrl ? String(storeUrl) : null);
    }
    return response;
  },
  (error) => {
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 426 &&
      error.response.data?.code === 'client_version_unsupported'
    ) {
      setUpdateRequired({
        minVersion: error.response.data.min_version ?? null,
        updateUrl: error.response.data.update_url ?? null,
      });
    }
    trackFailedRequest(error);
    return Promise.reject(error);
  },
);

export const returnAxiosValidationOrGeneralFetchError = (
  error: any,
): HttpError | ValidationResult[] => {
  if (axios.isAxiosError(error)) {
    if (
      Array.isArray(error.response?.data) &&
      error.response?.data.every((item) => typeof item === 'string')
    ) {
      return error.response?.data.map((e) => ({
        isValid: false,
        error: i18n.t(e),
      }));
    } else if (error.response?.data !== null && typeof error.response?.data === 'object') {
      const validationResults: ValidationResult[] = [];
      Object.entries(error.response.data).map(([key, value]) => {
        validationResults.push({
          isValid: false,
          error:
            typeof value === 'string'
              ? value
              : Array.isArray(value)
                ? i18n.t(value[0])
                : i18n.t('error-unknown-validation-error'),
          inputKey: key,
        });
        return null;
      });
      return validationResults;
    } else {
      return newHttpError(
        String(error.response?.status),
        error.response?.data != null &&
          typeof error.response.data === 'string' &&
          [400, 401, 403, 423].includes(error.response.status)
          ? `${error.response.data}`
          : i18n.t('error-unknown-server-error'),
        error.response?.data != null &&
          typeof error.response.data === 'string' &&
          error.response.status === 500
          ? `${error.response.data}`
          : null,
      );
    }
  }
  return newHttpError('FAILED', '');
};

export const returnAxiosOrGeneralFetchError = (error: any): HttpError => {
  if (axios.isAxiosError(error)) {
    return newHttpError(
      String(error.response?.status),
      error.response?.data != null &&
        typeof error.response.data === 'string' &&
        (error.response.status === 400 ||
          error.response.status === 503 ||
          error.response.status === 404)
        ? `${error.response.data}`
        : i18n.t('error-unknown-server-error'),
      error.response?.data != null &&
        typeof error.response.data === 'string' &&
        error.response.status === 500
        ? `${error.response.data}`
        : null,
    );
  }
  return newHttpError('FAILED', '');
};

export const mediaApiInstance = axios.create({
  baseURL: apiHost,
  headers: {
    'Accept-Language': getCurrentLanguage().languageCode,
  },
  responseType: 'blob',
});

mediaApiInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    trackFailedRequest(error);
    return Promise.reject(error);
  },
);

export const fetchMediaWithToken = async (url: string): Promise<string> => {
  try {
    const token = getToken();

    const response = await mediaApiInstance.get(url, {
      headers: {
        Authorization: `Token ${token}`,
      },
      responseType: 'blob',
    });

    if (response.status !== 200) {
      throw new Error('Failed to fetch image');
    }

    const objectURL = URL.createObjectURL(response.data);
    return objectURL;
  } catch (error) {
    console.error('Error fetching image:', error);
    throw error;
  }
};
